import { AxiosResponse } from "axios";
import { axiosInstance } from "./AxiosDefaults";
import {
  processingRequestsRoute,
  processingDispositionsRoute,
  processingOrdersRoute,
  operatorOrdersRoute,
  processingLogsRoute,
  declarationsRoute,
  oneDocumentRoute,
} from "../constants/ApiRoutes";
import { ProcessingRequestStatus } from "../models/ProcessingRequest";

export const getProcessingRequests = (params: {
  dateFrom?: Date;
  dateTo?: Date;
  productId?: string;
  partnerId?: string;
  status?: ProcessingRequestStatus;
}): Promise<AxiosResponse<any>> => {
  let route = processingRequestsRoute;
  const query: string[] = [];

  if (params.dateFrom && params.dateTo) {
    query.push(
      `datefrom=${params.dateFrom.toDateString()}`,
      `dateto=${params.dateTo.toDateString()}`,
    );
  }

  if (params.productId) {
    query.push(`productId=${params.productId}`);
  }

  if (params.partnerId) {
    query.push(`partnerId=${params.partnerId}`);
  }

  if (params.status) {
    query.push(`status=${params.status}`);
  }

  if (query.length > 0) {
    route += `?${query.join("&")}`;
  }

  return axiosInstance.get(route, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const getOneProcessingRequest = (id: string): Promise<any> => {
  return axiosInstance.get(`${processingRequestsRoute}/${id}`, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const addNewProcessingRequest = (data: any): Promise<any> => {
  return axiosInstance.post(processingRequestsRoute, data, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const updateOneProcessingRequest = (
  id: string,
  data: any,
): Promise<any> => {
  return axiosInstance.put(`${processingRequestsRoute}/${id}`, data, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const getNextProcessingRequestNumber = (year?: number): Promise<any> => {
  const url = year
    ? `${processingRequestsRoute}/next-number?year=${year}`
    : `${processingRequestsRoute}/next-number`;

  return axiosInstance.get(url, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const deleteOneProcessingRequest = (id: string): Promise<any> => {
  return axiosInstance.delete(`${processingRequestsRoute}/${id}`, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const createOwnershipDocumentForRequest = (
  requestId: string,
  amountKg: number,
): Promise<any> => {
  return axiosInstance.post(
    `${processingRequestsRoute}/${requestId}/ownership-document`,
    { amountKg },
    {
      withCredentials: false,
      headers: { "Content-Type": "application/json" },
    },
  );
};

export const transferBadQualityForRequest = (
  id: string,
  data: { newProductId: string; newWarehouseId: string },
): Promise<any> => {
  return axiosInstance.post(
    `${processingRequestsRoute}/${id}/bad-quality-transfer`,
    data,
    {
      withCredentials: false,
      headers: { "Content-Type": "application/json" },
    },
  );
};

export const getProcessingDispositions = (
  id?: string,
  dateFrom?: Date,
  dateTo?: Date,
): Promise<any> => {
  let route = processingDispositionsRoute;
  const params: string[] = [];

  if (id) params.push(`requestId=${id}`);
  if (dateFrom && dateTo) {
    params.push(`datefrom=${dateFrom.toDateString()}`);
    params.push(`dateto=${dateTo.toDateString()}`);
  }

  if (params.length > 0) {
    route += `?${params.join("&")}`;
  }

  return axiosInstance.get(route, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const getProcessingDispositionsByRequest = (
  requestId: string,
): Promise<any> => {
  return axiosInstance.get(
    `${processingDispositionsRoute}/by-request/${requestId}`,
    {
      withCredentials: false,
      headers: { "Content-Type": "application/json" },
    },
  );
};

export const getOneProcessingDisposition = (id: string): Promise<any> => {
  return axiosInstance.get(`${processingDispositionsRoute}/${id}`, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const addNewProcessingDisposition = (data: any): Promise<any> => {
  return axiosInstance.post(processingDispositionsRoute, data, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const updateOneProcessingDisposition = (
  id: string,
  data: any,
): Promise<any> => {
  return axiosInstance.put(`${processingDispositionsRoute}/${id}`, data, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const deleteOneProcessingDisposition = (id: string): Promise<any> => {
  return axiosInstance.delete(`${processingDispositionsRoute}/${id}`, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const getProcessingOrders = (
  dateFrom?: Date,
  dateTo?: Date,
): Promise<any> => {
  let route = processingOrdersRoute;
  const params: string[] = [];

  if (dateFrom && dateTo) {
    params.push(`datefrom=${dateFrom.toDateString()}`);
    params.push(`dateto=${dateTo.toDateString()}`);
  }

  if (params.length > 0) {
    route += `?${params.join("&")}`;
  }

  return axiosInstance.get(route, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const getOneProcessingOrder = (id: string): Promise<any> => {
  return axiosInstance.get(`${processingOrdersRoute}/${id}`, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const addNewProcessingOrder = (data: any): Promise<any> => {
  return axiosInstance.post(processingOrdersRoute, data, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const updateOneProcessingOrder = (
  id: string,
  data: any,
): Promise<any> => {
  return axiosInstance.put(`${processingOrdersRoute}/${id}`, data, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const deleteOneProcessingOrder = (id: string): Promise<any> => {
  return axiosInstance.delete(`${processingOrdersRoute}/${id}`, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const getOperatorOrders = (
  orderId?: string,
  dateFrom?: Date,
  dateTo?: Date,
): Promise<any> => {
  let route = operatorOrdersRoute;
  const params: string[] = [];

  if (orderId) params.push(`orderId=${orderId}`);

  if (dateFrom)
    params.push(`datefrom=${dateFrom?.toISOString().substring(0, 10)}`);
  if (dateTo) params.push(`dateto=${dateTo?.toISOString().substring(0, 10)}`);

  if (params.length > 0) {
    route += `?${params.join("&")}`;
  }

  return axiosInstance.get(route, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const getOneOperatorOrder = (id: string): Promise<any> => {
  return axiosInstance.get(`${operatorOrdersRoute}/${id}`, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const addNewOperatorOrder = (data: any): Promise<any> => {
  return axiosInstance.post(operatorOrdersRoute, data, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const updateOneOperatorOrder = (id: string, data: any): Promise<any> => {
  return axiosInstance.put(`${operatorOrdersRoute}/${id}`, data, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const deleteOneOperatorOrder = (id: string): Promise<any> => {
  return axiosInstance.delete(`${operatorOrdersRoute}/${id}`, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const getOperatorOrderConsumptions = (orderId: string) => {
  return axiosInstance.get(
    `${processingLogsRoute}/operator-order-consumptions?orderId=${orderId}`,
  );
};

export const getProcessingLogs = (
  requestId?: string,
  orderId?: string,
  dateFrom?: Date,
  dateTo?: Date,
): Promise<any> => {
  let route = processingLogsRoute;
  const params: string[] = [];

  if (requestId) params.push(`requestId=${requestId}`);
  if (orderId) params.push(`orderId=${orderId}`);
  if (dateFrom) {
    const df = new Date(
      dateFrom.getFullYear(),
      dateFrom.getMonth(),
      dateFrom.getDate(),
      0,
      0,
      0,
      0,
    );
    params.push(`datefrom=${encodeURIComponent(df.toISOString())}`);
  }

  if (dateTo) {
    const dt = new Date(
      dateTo.getFullYear(),
      dateTo.getMonth(),
      dateTo.getDate(),
      23,
      59,
      59,
      999,
    );
    params.push(`dateto=${encodeURIComponent(dt.toISOString())}`);
  }

  if (params.length > 0) {
    route += `?${params.join("&")}`;
  }

  return axiosInstance.get(route, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const getOneProcessingLog = (id: string): Promise<any> => {
  return axiosInstance.get(`${processingLogsRoute}/${id}`, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const addNewProcessingLog = (data: any): Promise<any> => {
  return axiosInstance.post(processingLogsRoute, data, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const updateOneProcessingLog = (id: string, data: any): Promise<any> => {
  return axiosInstance.put(`${processingLogsRoute}/${id}`, data, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const deleteOneProcessingLog = (id: string): Promise<any> => {
  return axiosInstance.delete(`${processingLogsRoute}/${id}`, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const getDeclarations = (
  dateFrom?: Date,
  dateTo?: Date,
): Promise<any> => {
  let route = declarationsRoute;
  const params: string[] = [];

  if (dateFrom) {
    const df = new Date(
      dateFrom.getFullYear(),
      dateFrom.getMonth(),
      dateFrom.getDate(),
      0,
      0,
      0,
      0,
    );
    params.push(`datefrom=${encodeURIComponent(df.toISOString())}`);
  }
  if (dateTo) {
    const dt = new Date(
      dateTo.getFullYear(),
      dateTo.getMonth(),
      dateTo.getDate(),
      23,
      59,
      59,
      999,
    );
    params.push(`dateto=${encodeURIComponent(dt.toISOString())}`);
  }

  if (params.length > 0) {
    route += `?${params.join("&")}`;
  }

  return axiosInstance.get(route, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const getOneDeclaration = (id: string): Promise<any> => {
  return axiosInstance.get(`${declarationsRoute}/${id}`, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const addNewDeclaration = (data: any): Promise<any> => {
  return axiosInstance.post(declarationsRoute, data, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const updateOneDeclaration = (id: string, data: any): Promise<any> => {
  return axiosInstance.put(`${declarationsRoute}/${id}`, data, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const deleteOneDeclaration = (id: string): Promise<any> => {
  return axiosInstance.delete(`${declarationsRoute}/${id}`, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const getProcessingOrdersForDeclarations = (
  dateFrom?: Date,
  dateTo?: Date,
): Promise<any> => {
  let route = `${processingOrdersRoute}/for-declarations`;
  const params: string[] = [];

  if (dateFrom) {
    const df = new Date(
      dateFrom.getFullYear(),
      dateFrom.getMonth(),
      dateFrom.getDate(),
      0,
      0,
      0,
      0,
    );
    params.push(`datefrom=${encodeURIComponent(df.toISOString())}`);
  }
  if (dateTo) {
    const dt = new Date(
      dateTo.getFullYear(),
      dateTo.getMonth(),
      dateTo.getDate(),
      23,
      59,
      59,
      999,
    );
    params.push(`dateto=${encodeURIComponent(dt.toISOString())}`);
  }

  if (params.length > 0) {
    route += `?${params.join("&")}`;
  }

  return axiosInstance.get(route, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const getNaturalStockReport = (
  dateFrom?: Date,
  dateTo?: Date,
): Promise<any> => {
  let route = `${oneDocumentRoute}/natural-stock`;
  const params: string[] = [];

  if (dateFrom) {
    params.push(`datefrom=${encodeURIComponent(dateFrom.toISOString())}`);
  }
  if (dateTo) {
    params.push(`dateto=${encodeURIComponent(dateTo.toISOString())}`);
  }

  if (params.length > 0) {
    route += `?${params.join("&")}`;
  }

  return axiosInstance.get(route, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const getSeedStockReport = (
  dateFrom?: Date,
  dateTo?: Date,
): Promise<any> => {
  let route = `${oneDocumentRoute}/seed-stock`;
  const params: string[] = [];

  if (dateFrom) {
    params.push(`datefrom=${encodeURIComponent(dateFrom.toISOString())}`);
  }
  if (dateTo) {
    params.push(`dateto=${encodeURIComponent(dateTo.toISOString())}`);
  }

  if (params.length > 0) {
    route += `?${params.join("&")}`;
  }

  return axiosInstance.get(route, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};
